import { resolveRenderableImage } from "../../utils/imageVariants";

const normalizeDateValue = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match?.[1]) return match[1];
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getCartItemKey = (item = {}) =>
  String(
    item.key ||
      item.metadata?.key ||
      item.uid ||
      item.serverId ||
      item._id ||
      "",
  ).trim();

export const stripRoomQuantitySuffix = (value = "") =>
  String(value || "").trim().replace(/\|Q\d+$/i, "");

export const parseRoomIdentifier = (identifier = "") => {
  const parts = String(identifier || "").split(":").filter(Boolean);
  if (parts.length < 3) {
    return { destinationSlug: "", campSlug: "", roomSlug: "" };
  }
  const [destinationSlug, campSlug, ...roomParts] = parts;
  return {
    destinationSlug,
    campSlug,
    roomSlug: roomParts.join(":"),
  };
};

export const resolveContinueHref = (entry = {}) => {
  const mode = String(entry.searchState?.mode || entry.type || "").toLowerCase();
  if (mode === "stays" || mode === "stay" || mode === "room") {
    const { destinationSlug, campSlug } = parseRoomIdentifier(entry.identifier);
    if (destinationSlug && campSlug) {
      return `/stays-camps/${encodeURIComponent(destinationSlug)}/${encodeURIComponent(campSlug)}`;
    }
    return entry.href || "/stays-camps";
  }
  return entry.href || "/experiences";
};

const buildCartEntry = (item = {}) => {
  const isStay = item.experienceType === "room";
  const cartItemKey = getCartItemKey(item);
  const startDate =
    item.checkIn ||
    item.metadata?.checkIn ||
    item.options?.checkIn ||
    item.travelDate ||
    item.date ||
    "";
  const endDate =
    item.checkOut || item.metadata?.checkOut || item.options?.checkOut || "";
  const identifier = String(item.identifier || item.id || "").trim();
  const entry = {
    key: `cart:${cartItemKey || identifier || item.title || "booking"}`,
    cartItemKey,
    storageKey: "",
    source: "cart",
    type: isStay ? "stay" : "experience",
    identifier,
    title: item.title || "Booking",
    subtitle: item.subtitle || item.destination?.title || item.entryPoint || "",
    image: resolveRenderableImage(item, "") || item.metadata?.image || "",
    href: item.link || item.metadata?.href || "",
    searchState: {
      mode: isStay ? "stays" : "experiences",
      experienceType: isStay ? "" : item.experienceType || "",
      experienceCategory: item.metadata?.categorySlug || "",
      startDate: normalizeDateValue(startDate),
      endDate: normalizeDateValue(endDate),
      guests: item.guestMix || item.options?.guestMix || null,
      location: {
        district: item.destination?.district || "",
        destinationSlug: item.destination?.slug || "",
        location: item.destination?.title || item.subtitle || "",
        campSlug: item.destination?.campSlug || item.metadata?.campSlug || "",
      },
    },
    quantity: Math.max(1, Number(item.quantity || item.options?.quantity || 1) || 1),
    updatedAt: Number(item.updatedAt) || Number(item.createdAt) || 0,
  };
  return { ...entry, href: resolveContinueHref(entry) };
};

const storageMatchesCartEntry = (storageEntry = {}, cartEntry = {}) => {
  if (!storageEntry.key || !cartEntry.cartItemKey) return false;
  return stripRoomQuantitySuffix(storageEntry.key) === cartEntry.cartItemKey;
};

export const buildContinueBookingEntries = (storedEntries = [], cartItems = []) => {
  const cartEntries = (Array.isArray(cartItems) ? cartItems : [])
    .map(buildCartEntry)
    .filter((entry) => entry.cartItemKey || entry.identifier);
  const unmatchedStored = [];

  (Array.isArray(storedEntries) ? storedEntries : []).forEach((storedEntry) => {
    if (!storedEntry?.key) return;
    const cartMatch = cartEntries.find((cartEntry) => storageMatchesCartEntry(storedEntry, cartEntry));
    if (!cartMatch) {
      const normalizedStored = {
        ...storedEntry,
        key: `stored:${storedEntry.key}`,
        storageKey: storedEntry.key,
        cartItemKey: "",
        source: "stored",
      };
      unmatchedStored.push({
        ...normalizedStored,
        href: resolveContinueHref(normalizedStored),
      });
      return;
    }

    cartMatch.storageKey = storedEntry.key;
    cartMatch.title = storedEntry.title || cartMatch.title;
    cartMatch.subtitle = storedEntry.subtitle || cartMatch.subtitle;
    // A current cart entity may carry a canonical cover. Never replace it
    // with an older local-storage snapshot; storage is fallback-only.
    cartMatch.image = cartMatch.image || storedEntry.image || "";
    cartMatch.href = resolveContinueHref({
      ...cartMatch,
      href: storedEntry.href || cartMatch.href,
    });
    cartMatch.searchState = storedEntry.searchState || cartMatch.searchState;
    cartMatch.updatedAt = Math.max(
      Number(cartMatch.updatedAt) || 0,
      Number(storedEntry.updatedAt) || 0,
    );
  });

  return [...cartEntries, ...unmatchedStored]
    .sort((left, right) => (Number(right.updatedAt) || 0) - (Number(left.updatedAt) || 0))
    .slice(0, 5);
};
